import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import { ProductType } from '@tink/core/src/types';
import { Button } from '../../../components/atoms/Button/Button';
import { Header } from '../../../components/organisms/Header/Header';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { Footer } from '../../../components/atoms/Footer/Footer';
import { H1, H2 } from '../../../components/atoms/Heading/Heading';
import { Stack } from '../../../components/atoms/Stack/Stack';
import { Caption } from '../../../components/atoms/Text/Text';
import { InformationField } from '../../../components/molecules/InformationField/InformationField';
import { PageView } from '../../../models/analytics';
import { DocumentOverlayLink } from '../../../components/screens/molecules/LegalDisclaimer/molecules/DocumentOverlayLink/DocumentOverlayLink';
import { getLanguageCode } from '../../../utilities/utils';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { messages as consentMessages } from '../../../components/screens/molecules/SubmitCredentialsScreen/screens/ConsentScreen/messages';
import { messages as legalDisclaimerMessage } from '../../../components/screens/molecules/LegalDisclaimer/LegalDisclaimerAuth/messages';
import { messages as disclaimerMessages } from '../../messages';
import { usePageViewEvents as usePageViewEventsImport } from '../../../core/Analytics/usePageViewEvents';
import { UnderlinedTextButton } from '../../../components/atoms/Button/TextButton';
import { ScopesDetailOverlay } from '../../../components/screens/molecules/LegalDisclaimer/LegalDisclaimerAuth/molecules/ScopesDetailOverlay/ScopesDetailOverlay';
import { ScopeItem } from '../../../lib-api/services/Oauth';
import { getTermsAndConditionsMarketUrl } from '../../../core/screens/Legal/TermsAndConditions/TermsAndConditionsMarket';
import { getPrivacyPolicyMarketUrl } from '../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicyMarket';
import {
  getConsentButtonText,
  getConsentDetails,
  getConsentTitle,
  getRiskConsentTermsAndConditions,
} from './consent-utils';

export type ConsentViewProps = {
  clientName: string;
  scopesDescriptionsList: ScopeItem[];
  onConfirm: () => void;
  usePageViewEvents?: ({ pageView }: { pageView: PageView }) => void;
  productType: ProductType;
};

export const ConsentView = ({
  onConfirm,
  clientName,
  scopesDescriptionsList,
  usePageViewEvents = usePageViewEventsImport,
  productType,
}: ConsentViewProps) => {
  const { locale, market } = useUrlQuery();
  const languageCode = getLanguageCode(locale);
  const consentTitle = getConsentTitle(productType);
  const consentDetails = getConsentDetails(market);
  const riskConsentTermsAndConditions = getRiskConsentTermsAndConditions(market);
  const buttonText = getConsentButtonText(market);

  const [isScopesDetailOverlayOpen, setScopesOverlayOpen] = useState(false);
  const openScopesModal = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setScopesOverlayOpen(true);
  };
  const closeScopesModal = () => setScopesOverlayOpen(false);

  usePageViewEvents({
    pageView: PageView.CONSENT_SCREEN,
  });

  return (
    <ScreenWrapper data-testid="consent-view">
      <Header hideLogo pageView={PageView.CONSENT_SCREEN} onBack={undefined} />
      <ContentBody>
        <Stack justifyContent="center" alignItems="center">
          <Box pb="normal">
            <H1 align="center">
              <FormattedMessage
                {...consentTitle}
                values={{
                  clientName,
                }}
              />
            </H1>
          </Box>
          <InformationField iconVariant="profile" isDecorated={false} iconColor="primary">
            <Box>
              <H2 mt="xxsmall" mb="xxsmall">
                <FormattedMessage {...consentMessages.aboutData} />
              </H2>
              <Caption>
                <FormattedMessage
                  {...consentDetails}
                  values={{
                    clientName,
                  }}
                />{' '}
                <UnderlinedTextButton onClick={openScopesModal} as="a" role="button" tabIndex={0}>
                  <FormattedMessage {...legalDisclaimerMessage.readMore} />
                </UnderlinedTextButton>
                {isScopesDetailOverlayOpen && (
                  <ScopesDetailOverlay close={closeScopesModal} scopes={scopesDescriptionsList} />
                )}
              </Caption>
            </Box>
          </InformationField>
          <InformationField iconVariant="lock-empty" isDecorated={false} iconColor="primary">
            <Box>
              <H2 mt="xsmall" mb="xxsmall">
                <FormattedMessage {...disclaimerMessages.termsAndPrivacy} />
              </H2>
              <Caption>
                <FormattedMessage
                  {...riskConsentTermsAndConditions}
                  values={{
                    termsAndConditions: (
                      <DocumentOverlayLink
                        documentUrl={getTermsAndConditionsMarketUrl({
                          market,
                          language: languageCode,
                        })}
                        pageView={PageView.TERMS_AND_CONDITION_SCREEN}
                      >
                        <FormattedMessage {...disclaimerMessages.termsAndConditions} />
                      </DocumentOverlayLink>
                    ),
                    privacyPolicy: (
                      <DocumentOverlayLink
                        documentUrl={getPrivacyPolicyMarketUrl({ market, language: languageCode })}
                        pageView={PageView.PRIVACY_POLICY_SCREEN}
                      >
                        <FormattedMessage {...disclaimerMessages.privacyPolicy} />
                      </DocumentOverlayLink>
                    ),
                  }}
                />
              </Caption>
            </Box>
          </InformationField>
        </Stack>
        <Footer stickToBottom>
          <Button
            size="large"
            type="submit"
            color="primary"
            variant="contained"
            onClick={onConfirm}
            mb="normedium"
            data-testid="consent-view-confirm-button"
          >
            <FormattedMessage {...buttonText} />
          </Button>
        </Footer>
      </ContentBody>
    </ScreenWrapper>
  );
};
